import { useEffect, useMemo, useState } from 'react';
import { PRICECODE_WEIGHTCONTROLLED } from '~/common/constants/ppConstants';
import { formatPrice } from '~/common/utils/numberUtil';
import { useTranslation } from 'react-i18next';
import type { AghArticle } from '~/common/types/aghArticle';
import { getSplittedArticlePrice, isArticleNumber } from '~/features/catalog/utils/articleUtil';
import { isFuture } from 'date-fns';
import { useSearchParams } from 'react-router';

export function useCatalogAghArticle(
  baseArticle: AghArticle,
  initialQuantity: undefined | number = 1,
  preSelectedArticleNumber?: string
) {
  const { i18n } = useTranslation();

  const [searchParams] = useSearchParams();

  const allArticles = baseArticle.isZzArticle ? [baseArticle, ...baseArticle.zzArticles] : [baseArticle];

  const [selectedArticleNumber, setSelectedArticleNumber] = useState<string>(() =>
    preSelectedArticleNumber ?? baseArticle.articleNumber
  );

  useEffect(() => {
    if (searchParams.has('searchTerm') && !preSelectedArticleNumber) {
      const searchTerm = searchParams.get('searchTerm');

      if (searchTerm) {
        if (isArticleNumber(searchTerm)) {
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setSelectedArticleNumber(searchTerm);
        } else {
          setSelectedArticleNumber(baseArticle.articleNumber);
        }
      }
    }
    if (preSelectedArticleNumber) {
      setSelectedArticleNumber(preSelectedArticleNumber);
    }
  }, [preSelectedArticleNumber, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const [quantity, setQuantity] = useState<number>(initialQuantity);

  const article = allArticles.find(a => a.articleNumber === selectedArticleNumber) ?? baseArticle;

  const price = getSplittedArticlePrice(article.price);

  const calculatedTotalPriceText = useMemo(() => {
    const currentQuantity = Number.isNaN(quantity) ? 0 : quantity;

    const priceValue = article.price;

    const total =
      currentQuantity *
      (article.sellAmount *
        (article.priceCode === PRICECODE_WEIGHTCONTROLLED ? article.approxWeight! : 1) *
        priceValue);

    return `CHF ${formatPrice(total, i18n.language)}`;
  }, [article.approxWeight, article.price, article.priceCode, article.sellAmount, i18n.language, quantity]);

  return {
    ...article,
    price,
    setSelectedArticleNumber,
    allArticles,
    calculatedTotalPriceText,
    quantity,
    setQuantity,
    isFutureAction: article.actionValidFrom ? isFuture(article.actionValidFrom) : undefined
  };
}
